import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Acl } from 'src/app/models/acl';
import { ImportReport } from 'src/app/models/gum-models/import-report.model';
import { ListOptions } from 'src/app/models/gum-models/list-options';
import { Organization } from 'src/app/models/organization.model';
import { Property } from 'src/app/models/property';
import { IimportableService } from './iimportable-service';
import { ServiceBase } from '../service-base';
import { GumOrganizationModel } from 'src/app/models/gum-models/gum-organization.model';

@Injectable({
  providedIn: 'root',
})
export class GumOrganizationsService
  extends ServiceBase
  implements IimportableService
{
  private importFinished = new Subject<ImportReport>();

  private currentOrganizationSubject: BehaviorSubject<Organization | null>;

  constructor(private http: HttpClient) {
    super();
    const currentOrgJson = sessionStorage.getItem('currentOrganization');
    const currentOrg = currentOrgJson
      ? (JSON.parse(currentOrgJson) as Organization)
      : null;
    this.currentOrganizationSubject = new BehaviorSubject<Organization | null>(
      currentOrg
    );
  }

  public get onImportFinished$() {
    return this.importFinished.asObservable();
  }

  public get currentOrganizationChange$() {
    return this.currentOrganizationSubject.asObservable();
  }

  getOrganisation(
    organizationId: string,
    globalNotification = true,
    executeOption = false
  ): Observable<Organization> {
    const orgId =
      executeOption && this.currentOrganizationSubject.value?.id
        ? this.currentOrganizationSubject.value.id
        : '';
    const httpOptions = {
      headers: new HttpHeaders()
        .set('global-notification', String(globalNotification))
        .set('X-OrganizationId', orgId),
    };

    return this.http.get<Organization>(
      `${this.apiUrl}/api/gum/organizations/${organizationId}`,
      httpOptions
    );
  }

  getOrganizationWithDisplayParentOrganization(
    organizationId: string
  ): Observable<Organization> {
    return this.getOrganisation(organizationId).pipe(
      switchMap((organization: Organization) => {
        console.log('[DEBUG] Organisation récupérée :', organization);

        const parentId = organization.organizationId;

        if (!parentId) {
          console.warn('[WARN] Aucune organizationId définie.');
          organization.displayParentOrganization = '';
          return of(organization);
        }

        return this.getOrganisation(parentId, false).pipe(
          map((parentOrg) => {
            organization.displayParentOrganization = parentOrg.display;
            return organization;
          }),
          catchError((err) => {
            console.error('[ERREUR] Impossible de récupérer la parentOrg', {
              parentId,
              err,
            });
            organization.displayParentOrganization = `[${parentId}]`;
            return of(organization);
          })
        );
      })
    );
  }

  getOrganisations(
    executeOption: boolean = true,
    filter: string | null = null,
    listOptions?: ListOptions
  ): Observable<Organization[]> {
    const orgId =
      executeOption && this.currentOrganizationSubject.value?.id
        ? this.currentOrganizationSubject.value.id
        : '';
    let headers = new HttpHeaders().set('X-OrganizationId', orgId);

    if (listOptions) {
      headers = listOptions.addListOptions(headers);
    }

    const urlString = filter
      ? `${this.apiUrl}/api/gum/organizations?filter=${encodeURIComponent(
          filter
        )}`
      : `${this.apiUrl}/api/gum/organizations`;

    return this.http.get<Organization[]>(urlString, { headers }).pipe(
      map((orgs) => {
        orgs.forEach((org) => {
          const parentOrg = orgs.find((x) => x.id === org.organizationId);
          org.displayParentOrganization = parentOrg ? parentOrg.display : '';
        });
        return orgs;
      })
    );
  }

  addOrganization(
    organization: GumOrganizationModel
  ): Observable<Organization> {
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('X-OrganizationId', this.currentOrganizationSubject.value?.id ?? '');

    return this.http.post<Organization>(
      `${this.apiUrl}/api/gum/organizations`,
      organization,
      { headers }
    );
  }

  addOrganizations(
    organizations: Organization[],
    globalNotification = true
  ): Observable<ImportReport> {
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('global-notification', String(globalNotification))
      .set('X-OrganizationId', this.currentOrganizationSubject.value?.id ?? '');

    return this.http.post<ImportReport>(
      `${this.apiUrl}/api/gum/organizations/import`,
      organizations,
      { headers }
    );
  }

  deleteOrganization(organizationId: string) {
    const headers = new HttpHeaders().set(
      'X-OrganizationId',
      this.currentOrganizationSubject.value?.id ?? ''
    );
    return this.http.delete(
      `${this.apiUrl}/api/gum/organizations/${organizationId}`,
      { headers }
    );
  }

  deleteOrganizationProperty(organizationId: string, propertyName: string) {
    const headers = new HttpHeaders().set(
      'X-OrganizationId',
      this.currentOrganizationSubject.value?.id ?? ''
    );
    return this.http.delete(
      `${this.apiUrl}/api/gum/organizations/${organizationId}/properties/${propertyName}`,
      { headers }
    );
  }

  deleteOrganizationRight(organizationId: string, rightName: string) {
    const headers = new HttpHeaders().set(
      'X-OrganizationId',
      this.currentOrganizationSubject.value?.id ?? ''
    );
    return this.http.delete(
      `${this.apiUrl}/api/gum/organizations/${organizationId}/rights/${rightName}`,
      { headers }
    );
  }

  updateOrganization(
    id: string,
    organization: GumOrganizationModel
  ): Observable<Organization> {
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('X-OrganizationId', this.currentOrganizationSubject.value?.id ?? '');

    return this.http.patch<Organization>(
      `${this.apiUrl}/api/gum/organizations/${id}`,
      organization,
      { headers }
    );
  }

  updateOrganizationProperty(
    organizationId: string,
    propertyName: string,
    propertyValue: string
  ): Observable<Property> {
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('X-OrganizationId', this.currentOrganizationSubject.value?.id ?? '');

    return this.http.put<Property>(
      `${this.apiUrl}/api/gum/organizations/${organizationId}/properties/${propertyName}`,
      JSON.stringify(propertyValue),
      { headers }
    );
  }

  updateOrganizationRight(
    organizationId: string,
    rightName: string,
    rightValue: number
  ): Observable<Property> {
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('X-OrganizationId', this.currentOrganizationSubject.value?.id ?? '');

    return this.http.put<Property>(
      `${this.apiUrl}/api/gum/organizations/${organizationId}/rights/${rightName}`,
      rightValue,
      { headers }
    );
  }

  getCurrentOrganization(): Organization | null {
    return this.currentOrganizationSubject.value;
  }

  setCurrentOrganization(organization: Organization): void {
    sessionStorage.setItem('currentOrganization', JSON.stringify(organization));
    this.currentOrganizationSubject.next(organization);
  }

  removeCurrentOrganization(): void {
    sessionStorage.removeItem('currentOrganization');
    this.currentOrganizationSubject.next(null);
  }

  import(datas: any, properties?: any, rights?: any): void {
    const orgsToAdd: Organization[] = [];

    try {
      let datasToAdd = datas as Organization[];

      const propertiesOrgs =
        (properties as {
          organization: string;
          name: string;
          value: string;
        }[]) || [];
      const rightsOrgs =
        (rights as { organization: string; name: string; value: number }[]) ||
        [];

      datasToAdd = this.getTree(datasToAdd);

      datasToAdd.forEach((org) => {
        const newOrg: Organization = new Organization();
        newOrg.organizationId = this.getCurrentOrganization()?.id ?? '';
        newOrg.display = org.display?.trim() ?? '';
        newOrg.billing = org.billing?.trim() ?? '';
        newOrg.appData = org.appData?.trim() ?? '';

        newOrg.properties = propertiesOrgs
          .filter((p) => p.organization === org.display)
          .map((a) => {
            const property = new Property();
            property.name = a.name.trim();
            property.value = a.value?.trim() ?? '';
            return property;
          });

        newOrg.rights = rightsOrgs
          .filter((r) => r.organization === org.display)
          .map((a) => {
            const right = new Acl();
            right.name = a.name.trim();
            right.value = a.value;
            return right;
          });

        orgsToAdd.push(newOrg);
      });

      this.addOrganizations(orgsToAdd, false).subscribe(
        (report) => this.importFinished.next(report),
        () => {
          const error = new ImportReport();
          error.isImportError = true;
          this.importFinished.next(error);
        }
      );
    } catch (e) {
      const fileError = new ImportReport();
      fileError.isFileTreatmentError = true;
      this.importFinished.next(fileError);
    }
  }

  private getTree(organizations: Organization[]): Organization[] {
    const organizationsList = [...organizations];
    const organizationsSorted: Organization[] = [];

    while (organizationsList.length > 0) {
      const root = organizationsList.find(
        (o) =>
          o.organizationId ||
          (!o.organizationId && !o.displayParentOrganization)
      );
      if (!root) break;
      this.searchChildren(organizationsList, organizationsSorted, root);
    }

    return organizationsSorted;
  }

  private searchChildren(
    source: Organization[],
    result: Organization[],
    root: Organization
  ): void {
    result.push(root);
    source.splice(
      source.findIndex((o) => o.display === root.display),
      1
    );

    source
      .filter((o) => o.displayParentOrganization === root.display)
      .forEach((child) => this.searchChildren(source, result, child));
  }

  /** Détermine si l'organisation courante est un client */
  isCurrentOrganizationClient(): boolean {
    const org = this.getCurrentOrganization();
    if (!org) return false;

    const typeProp = org.properties
      ?.find((p) => p.name === 'nexus.type')
      ?.value?.toLowerCase();
    return typeProp === 'client';
  }

  /** Détermine si l'organisation courante est un partenaire */
  isCurrentOrganizationPartner(): boolean {
    const org = this.getCurrentOrganization();
    if (!org) return false;

    const typeProp = org.properties
      ?.find((p) => p.name === 'nexus.type')
      ?.value?.toLowerCase();
    return typeProp === 'partenaire';
  }
}
